import { LinkCellData } from '@widgets/innogy-data-table/link-cell/link-cell.component';

export interface BusinessPartner {
  id: string;
  name: string;
  locations: PartnerLocation[];
}

export interface PartnerLocation {
  id: string;
  name: string;
}

export interface BusinessPartners {
  businessPartners: BusinessPartner[];
}

export interface Alert {
  id: number;
  chargingStationId: string;
  chargingStationName: string;
  created: string;
  deviceType: DeviceType;
  deviceId: string;
  alertStatus: AlertStatus;
  alertType: AlertType;
  alertSubType?: AlertSubType;
  errorCode?: AlertErrorCode;
  details?: string;
  groups?: string[];
  resolved?: string;
  isStationMuted?: boolean;
  mutedUntil?: string;
  address: AlertStationAddress;
}

export interface AlertDetails {
  alertId: number;
  description?: string;
  caseNumber?: string;
  created?: string;
  reporter?: AlertReporter;
  sessionAlertDetails?: ChargingSessionAlertDetails;
}

export interface SelectedAlert extends AlertDetails {
  isEvDriverIncident: boolean;
}

export interface AlertReporter {
  email: string;
  name: string;
  phone: string;
}

export interface AlertFilterEvent {
  partnerId: string;
  partnerLocationId?: string;
  alertStatus?: AlertStatus | AlertStatus[];
  alertType?: AlertType | AlertType[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  stationId?: string;
  stationGroupId?: string | string[];
}

export interface AlertFilter extends AlertFilterEvent {
  page?: number;
  sortBy: SortColumn;
}

export interface MutedAlertFilterEvent {
  partnerId: string;
  partnerLocationId?: string;
  search?: string;
  stationId?: string;
}

export interface MutedAlertFilter extends MutedAlertFilterEvent {
  page?: number;
  sortBy: SortColumn;
}

export interface MutedStation {
  stationId: string;
  stationName: string;
  mutedSince: string;
  mutedUntil: string;
  address: MutedStationAddress;
  userNote?: string;
}

export interface MutedStationRow {
  stationId: string;
  stationName: LinkCellData;
  address: string;
  muteInformation: string;
  userNote: string;
}

export interface MutedStationAddress {
  country: string;
  zipCode: string;
  city: string;
  street: string;
  supplementLocal: string;
  supplementEN: string;
  locationType: string;
}

export interface MutedStationsResponse {
  mutedStations: MutedStation[];
  paging: Paging;
}

export interface MutedStationsTableData {
  paging: Paging;
  data: MutedStation[];
}

export interface Paging {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export interface AlertResponse {
  alerts: Alert[];
  paging: Paging;
}

export enum DeviceType {
  STATION = 'STATION',
  EVSE = 'EVSE',
}

export enum AlertType {
  UNAVAILABILITY = 'UNAVAILABILITY',
  ERROR_STATUS = 'ERROR_STATUS',
  SALESFORCE_TICKET = 'SALESFORCE_TICKET',
  WEBSOCKET_AUTH = 'WEBSOCKET_AUTH',
  CHARGING_SESSION = 'CHARGING_SESSION',
}

export enum AlertSubType {
  INOPERATIVE = 'INOPERATIVE',
  ERROR = 'ERROR',
  UNAVAILABLE = 'UNAVAILABLE',
  NO_HEARTBEAT = 'NO_HEARTBEAT',
  CS_MISSED_REVENUE = 'CS_MISSED_REVENUE',
  CS_LOST_REVENUE = 'CS_LOST_REVENUE',
  CS_INACTIVITY = 'CS_INACTIVITY',
  CS_INACTIVITY_PT = 'CS_INACTIVITY_PT',
  UNKNOWN = 'UNKNOWN',
}

export enum AlertErrorCode {
  ABORTED = 'ABORTED',
  CHARGING_FAILED = 'CHARGING_FAILED',
  CONNECTOR_LOCK_FAILURE = 'CONNECTOR_LOCK_FAILURE',
  CORRUPTED_FORMAT = 'CORRUPTED_FORMAT',
  DUPLICATE_OCMF = 'DUPLICATE_OCMF',
  EV_COMMUNICATION_ERROR = 'EV_COMMUNICATION_ERROR',
  EVSE_UNKNOWN = 'EVSE_UNKNOWN',
  GROUND_FAILURE = 'GROUND_FAILURE',
  HIGH_TEMPERATURE = 'HIGH_TEMPERATURE',
  INCOMPLETE = 'INCOMPLETE',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  LOCAL_LIST_CONFLICT = 'LOCAL_LIST_CONFLICT',
  NEGATIVE_CONSUMPTION = 'NEGATIVE_CONSUMPTION',
  NEGATIVE_DURATION = 'NEGATIVE_DURATION',
  NO_METER = 'NO_METER',
  NO_ERROR = 'NO_ERROR',
  OTHER_ERROR = 'OTHER_ERROR',
  OVER_CURRENT_FAILURE = 'OVER_CURRENT_FAILURE',
  POWER_METER_FAILURE = 'POWER_METER_FAILURE',
  POWER_SWITCH_FAILURE = 'POWER_SWITCH_FAILURE',
  POWER_IMPLAUSIBLE_FOR_EVSE = 'POWER_IMPLAUSIBLE_FOR_EVSE',
  PERISHED = 'PERISHED',
  READER_FAILURE = 'READER_FAILURE',
  RESET_FAILURE = 'RESET_FAILURE',
  RFID_UNAUTHORIZED = 'RFID_UNAUTHORIZED',
  SDR_INVALID = 'SDR_INVALID',
  SESSION_START_ABORTED = 'SESSION_START_ABORTED',
  SESSION_START_IN_FUTURE = 'SESSION_START_IN_FUTURE',
  START_ACTIVATION_FAILED = 'START_ACTIVATION_FAILED',
  START_REQUEST_FAILED = 'START_REQUEST_FAILED',
  STOP_ACTIVATION_FAILED = 'STOP_ACTIVATION_FAILED',
  STOP_REQUEST_FAILED = 'STOP_REQUEST_FAILED',
  UNDER_VOLTAGE = 'UNDER_VOLTAGE',
  OVER_VOLTAGE = 'OVER_VOLTAGE',
  WEAK_SIGNAL = 'WEAK_SIGNAL',
  MODE_3_ERROR = 'MODE_3_ERROR',
  STATION_ERROR = 'STATION_ERROR',
  FUSE_TRIPPED = 'FUSE_TRIPPED',
  UNKNOWN = 'UNKNOWN',
}

export enum ChargingSessionCurrentType {
  AC = 'AC',
  DC = 'DC',
  UNSPECIFIED = 'UNSPECIFIED',
}

export interface ChargingSessionAlertDetails {
  inactivityDetail?: ChargingSessionInactivityDetails;
  revenueDetail?: ChargingSessionRevenueDetails;
}

export interface ChargingSessionInactivityDetails {
  currentType: ChargingSessionCurrentType;
  daysSinceLastSession: number;
  thresholdDays: number;
}

export interface ChargingSessionRevenueDetails {
  consecutiveCount?: number;
  errors?: ChargingSessionErrorCount[];
  thresholdCount?: number;
}

export interface ChargingSessionErrorCount {
  errorCode: AlertErrorCode;
  count: number;
}

export enum AlertStatus {
  ACTIVE = 'ACTIVE',
  RESOLVED = 'RESOLVED',
}

export enum SortColumn {
  ACTIVE_FIRST = 'ACTIVE_FIRST',
  RESOLVED_FIRST = 'RESOLVED_FIRST',
  DATE_ASC = 'DATE_ASC',
  DATE_DESC = 'DATE_DESC',
}

export interface Sorting {
  sortBy: SortColumn;
}

export interface AlertPatchPayload {
  alertStatus?: AlertStatus;
}

export interface AlertGroup {
  id: number;
  name: string;
  isLinkedWithConfig: boolean;
  stations: AlertStation[];
}

export interface AlertStation {
  id: string;
  name: string;
  address: AlertStationAddress;
}

export interface AlertStationAddress {
  street: string;
  city: string;
  zipCode: string;
  country: string;
  supplementLocal: string;
  supplementEN: string;
}

export interface FilterAlertsPayload {
  search: string;
  stationId: string;
  locationId: string;
  from: string;
  to: string;
  alertTypes: string[];
  alertStatuses: string[];
  stationGroupIds: string[];
}

export interface Filters {
  currentPage?: number;
  itemsPerPage?: number;

  search?: string;

  city?: string;
  country?: string;
  streetSearchList?: string[];
  zipCode?: string;

  authenticationTypeList?: string[];
  chargingType?: string;
  connectorTypeList?: string[];
  detailStatusList?: string[];
  firmwareVersionList?: string[];
  protocolList?: string[];
  statusList?: string[];
}

export interface UiFilter {
  city?: string;
  country?: string;
  streetSearchList?: string[];
  zipCode?: string;

  authenticationTypeList?: string[];
  chargingType?: string;
  connectorTypeList?: string[];
  detailStatusList?: string[];
  firmwareVersionList?: string[];
  protocolList?: string[];
  statusList?: string[];
}

export interface UIPaging {
  page: number;
  pageSize?: number;
}

export interface ChargingStationsPaging {
  paging: Paging;
  chargingStations: ChargingStationSimple[];
}

export interface ChargingStationSimple {
  id: string;
  name: string;
  address: Address;
}

export interface Address {
  country: string;
  zipCode: string;
  city: string;
  street: string;
  supplementLocal: string;
  supplementEN: string;
}

export interface AlertGroupEditRequest {
  name: string;
  stationIds: string[];
}

export interface AlertConfiguration {
  cpoId?: string;
  alertType: AlertType;
  active: boolean;
  threshold: number;
  sendEmails: boolean;
  unit: ThresholdUnit;
  sendResolvedEmail: boolean;
  resendIntervalHours: number;
  stationRecipientGroups: StationRecipientGroup[];
  subtypeConfigs?: ChargingSessionSubtypeConfig[];
}

export interface UpdateAlertConfiguration {
  active: boolean;
  threshold: number;
  unit: ThresholdUnit;
  sendEmails: boolean;
  sendResolvedEmail: boolean;
  resendIntervalHours: number;
  stationRecipientGroups: UpdateStationRecipientGroup[];
  subtypeConfigs?: UpdateAlertConfigurationSubtype[];
}

export interface UpdateStationRecipientGroup {
  name: string;
  stationGroups: number[];
  emailGroups: number[];
}

export interface UpdateAlertConfigurationSubtype {
  subtype: ChargingSessionAlertSubtype;
  active: boolean;
  noSessionForAC?: number | null;
  noSessionForDC?: number | null;
  consecutiveFailures?: number | null;
}

export enum ThresholdUnit {
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
  PER_MINUTE = 'PER_MINUTE',
  PER_HOUR = 'PER_HOUR',
  PER_DAY = 'PER_DAY',
  COUNT = 'COUNT',
}

export enum ChargingSessionAlertSubtype {
  CS_INACTIVITY = 'CS_INACTIVITY',
  CS_INACTIVITY_PT = 'CS_INACTIVITY_PT',
  CS_MISSED_REVENUE = 'CS_MISSED_REVENUE',
  CS_LOST_REVENUE = 'CS_LOST_REVENUE',
}

export interface ChargingSessionSubtypeConfigBase {
  subtype: ChargingSessionAlertSubtype;
  active: boolean;
}

export interface ChargingSessionInactivitySubtypeConfig extends ChargingSessionSubtypeConfigBase {
  subtype: ChargingSessionAlertSubtype.CS_INACTIVITY | ChargingSessionAlertSubtype.CS_INACTIVITY_PT;
  noSessionForAC: number;
  noSessionForDC: number;
}

export interface ChargingSessionRevenueSubtypeConfig extends ChargingSessionSubtypeConfigBase {
  subtype:
    | ChargingSessionAlertSubtype.CS_MISSED_REVENUE
    | ChargingSessionAlertSubtype.CS_LOST_REVENUE;
  consecutiveFailures: number;
}

export type ChargingSessionSubtypeConfig =
  | ChargingSessionInactivitySubtypeConfig
  | ChargingSessionRevenueSubtypeConfig;

export interface StationRecipientGroup {
  name: string;
  stationGroups: StationGroup[];
  emailGroups: RecipientGroup[];
}

export interface StationGroup {
  id: number;
  name: string;
  isReadOnly?: boolean;
}

export interface RecipientGroup {
  id: number;
  name: string;
}

export interface Group {
  id: number;
  name: string;
}

export interface AlertRecipientGroup {
  id: number;
  name: string;
  cpoId: string;
  emails: string[];
  isLinkedWithConfig: boolean;
}

export interface AlertRecipientGroupCreateRequest {
  name: string;
  emails: string[];
}

export interface AlertRecipientGroupUpdateRequest {
  id: number;
  name: string;
  emails: string[];
}

export const GET_ALERTS_DEFAULT_PAGE_SIZE = 100;
export const GET_ALERTS_DEFAULT_DEFAULT_SORT_COLUMN = SortColumn.DATE_DESC;
export const GET_MUTED_STATIONS_DEFAULT_PAGE_SIZE = 100;

export interface FirmwareVersions {
  firmwareVersions: string[];
}

export interface ConnectorTypes {
  connectorTypes: string[];
}

export interface Protocols {
  protocols: string[];
}

export interface ChargingTypes {
  chargingTypes: string[];
}

export interface AuthenticationTypes {
  authenticationTypes: string[];
}

export interface Manufacturers {
  manufacturers: string[];
}

export interface ModelInfos {
  modelInfos: string[];
}

export interface MuteStationRequest {
  mutedUntil?: string;
  userNote?: string;
}
